import {getCoreRowModel, getExpandedRowModel, useReactTable as useReactTableLibrary} from '@tanstack/react-table'
import {useCallback, useMemo} from 'react'

import {useTableHierarchy} from '../../../features/sub-issues/use-table-hierarchy'
import {getCustomGroupedRowModel} from '../../../hooks/tanstack-table/row-models/get-custom-grouped-row-model'
import type {ColumnDef, Row} from '../../../hooks/tanstack-table/types'
import {useColumnSizingInfo} from '../../../hooks/tanstack-table/use-column-sizing-info'
import {controlledStateErrorWarnings, useTableState} from '../../../hooks/tanstack-table/use-table-state'
import type {ColumnModel} from '../../../models/column-model'
import {useMemexItems} from '../../../state-providers/memex-items/use-memex-items'
import {MIN_CELL_WIDTH} from '../constants'
import {getRowId} from '../get-row-id'
import {RowDraggerColumnDef, RowExpandColumnDef} from '../state-providers/table-columns/table-columns-provider'
import {useTableColumns} from '../state-providers/table-columns/use-table-columns'
import type {TableDataType} from '../table-data-type'
import {isRowSelectable} from '../use-row-selection'

type TableProps = {
  fields: ReadonlyArray<ColumnModel>
  pinnedRightColumnDefs?: ReadonlyArray<ColumnDef<TableDataType>>
}

export function useReactTable({fields, pinnedRightColumnDefs}: TableProps) {
  return useTanStackReactTable({fields, pinnedRightColumnDefs})
}

const useTanStackReactTable = ({
  fields,
  pinnedRightColumnDefs,
}: Pick<TableProps, 'fields' | 'pinnedRightColumnDefs'>) => {
  const readonlyData = useReactTableData()
  // Replace with mutable array while maintaining referential equality checks
  const data = useMemo(() => [...readonlyData], [readonlyData])
  const {columnDefinitions, columnVisibility} = useTableColumns()
  const {columnSizingInfo, onColumnSizingInfoChange} = useColumnSizingInfo()
  const {hierarchyEnabled} = useTableHierarchy()

  const pinnedLeftColumnDefs: ReadonlyArray<ColumnDef<TableDataType>> = useMemo(() => {
    if (hierarchyEnabled) {
      return [RowDraggerColumnDef, RowExpandColumnDef]
    } else {
      return [RowDraggerColumnDef]
    }
  }, [hierarchyEnabled])

  const getRowCanExpand = useCallback(
    (row: Row<TableDataType>) => {
      if (!hierarchyEnabled) return false
      const subIssuesProgress = row.original.getSubIssuesProgress()
      if (subIssuesProgress && subIssuesProgress.total > 0) {
        return true
      }
      return false
    },
    [hierarchyEnabled],
  )

  const allColumnDefs = useMemo(
    () => [...pinnedLeftColumnDefs, ...columnDefinitions, ...(pinnedRightColumnDefs ?? [])],
    [columnDefinitions, pinnedLeftColumnDefs, pinnedRightColumnDefs],
  )

  const {expanded, grouping, groupQueryData, sorting} = useTableState()
  const columnOrder = useMemo(
    () => [
      ...pinnedLeftColumnDefs.map(def => def.id),
      ...fields.map(f => f.id.toString()),
      ...(pinnedRightColumnDefs?.map(def => def.id) ?? []),
    ],
    [fields, pinnedLeftColumnDefs, pinnedRightColumnDefs],
  )

  return useReactTableLibrary<TableDataType>({
    columns: allColumnDefs,
    data,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getCustomGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand,
    enableRowSelection: isRowSelectable,
    manualSorting: true,
    columnResizeMode: 'onChange',
    defaultColumn: {minSize: MIN_CELL_WIDTH},
    autoResetAll: false,
    groupedColumnMode: false,
    initialState: {
      columnPinning: {
        left: pinnedLeftColumnDefs?.map(def => def.id),
        right: pinnedRightColumnDefs?.map(def => def.id),
      },
    },
    state: {
      columnVisibility,
      columnOrder,
      columnSizingInfo,
      expanded,
      grouping,
      groupQueryData,
      sorting,
    },
    onColumnSizingInfoChange,
    ...controlledStateErrorWarnings,
  })
}

function useReactTableData() {
  const {items} = useMemexItems()
  return useMemo(() => items, [items])
}
